import React, { useState, useRef, useEffect } from 'react';
import { MoreHorizontal, Pencil, Trash2, ArrowRight, Calendar, GripVertical } from 'lucide-react';
import { useTasks } from '../context/TaskContext';

export const formatDate = (d) => {
  if (!d) return '';
  const dt = new Date(d + 'T00:00:00');
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const PRIORITY_STYLES = {
  High: 'text-red-400 border-red-500/40 bg-red-500/10',
  Medium: 'text-amber-400 border-amber-500/40 bg-amber-500/10',
  Low: 'text-emerald-400 border-emerald-500/40 bg-emerald-500/10',
};

const PRIORITY_DOTS = {
  High: 'bg-red-500',
  Medium: 'bg-amber-500',
  Low: 'bg-emerald-500',
};

const CATEGORY_STYLES = 'text-indigo-400 border-indigo-500/40 bg-indigo-500/10';

const COLS = ['To Do', 'In Progress', 'Completed'];

const TaskCard = ({ task, onEdit, provided, snapshot }) => {
  const { deleteTask, moveTask, getDeadlineStatus } = useTasks();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const ds = getDeadlineStatus(task.deadline, task.status);

  useEffect(() => {
    const h = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const borderClass = task.status !== 'Completed'
    ? ds === 'overdue' ? 'border-l-2 border-l-red-500' : ds === 'near' ? 'border-l-2 border-l-amber-500' : ''
    : '';

  return (
    <div
      ref={provided.innerRef}
      {...provided.draggableProps}
      className={`task-card-hover group bg-white dark:bg-[#1e2235] border border-gray-200 dark:border-gray-700/60 rounded-xl p-3.5 cursor-grab active:cursor-grabbing select-none ${borderClass} ${snapshot.isDragging ? 'shadow-2xl shadow-indigo-500/20 rotate-1 opacity-90 border-indigo-500/50' : 'hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-lg dark:hover:shadow-black/30'}`}
    >
      <div className="flex items-start gap-2">
        {/* Drag handle */}
        <div {...provided.dragHandleProps} className="mt-0.5 text-gray-300 dark:text-gray-600 hover:text-gray-400 dark:hover:text-gray-400 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
          <GripVertical size={14} />
        </div>

        <div className="flex-1 min-w-0">
          {/* Title row */}
          <div className="flex items-start justify-between gap-2 mb-1.5">
            <h3 className={`text-sm font-medium text-gray-800 dark:text-gray-100 leading-snug ${task.status === 'Completed' ? 'line-through opacity-50' : ''}`}>
              {task.title}
            </h3>
            <div className="relative flex-shrink-0" ref={menuRef}>
              <button
                onClick={() => setMenuOpen(v => !v)}
                className="w-6 h-6 flex items-center justify-center rounded-md text-gray-300 dark:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-500 dark:hover:text-gray-300 transition-colors opacity-0 group-hover:opacity-100"
              >
                <MoreHorizontal size={14} />
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-7 w-40 bg-white dark:bg-[#22263a] border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-50 animate-scale-in overflow-hidden py-1">
                  <button onClick={() => { onEdit(task); setMenuOpen(false); }} className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <Pencil size={12} /> Edit task
                  </button>
                  {COLS.filter(s => s !== task.status).map(s => (
                    <button key={s} onClick={() => { moveTask(task.id, s); setMenuOpen(false); }} className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                      <ArrowRight size={12} /> Move to {s}
                    </button>
                  ))}
                  <div className="h-px bg-gray-100 dark:bg-gray-700 my-1" />
                  <button onClick={() => { deleteTask(task.id); setMenuOpen(false); }} className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors">
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          {task.description && (
            <p className="text-xs text-gray-400 dark:text-gray-500 leading-relaxed mb-2.5 line-clamp-2">{task.description}</p>
          )}

          {/* Badges + date */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${PRIORITY_STYLES[task.priority]}`}>
              <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${PRIORITY_DOTS[task.priority]}`} />
              {task.priority}
            </span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${CATEGORY_STYLES}`}>
              {task.category}
            </span>
            {task.deadline && (
              <span className={`text-[10px] flex items-center gap-1 ml-auto ${ds === 'overdue' && task.status !== 'Completed' ? 'text-red-400' : ds === 'near' && task.status !== 'Completed' ? 'text-amber-400' : 'text-gray-400 dark:text-gray-500'}`}>
                <Calendar size={10} />
                {formatDate(task.deadline)}
                {ds === 'overdue' && task.status !== 'Completed' && ' · Overdue'}
                {ds === 'near' && task.status !== 'Completed' && ' · Soon'}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskCard;
